/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
package i18n

import i18n.translations.englishStrings
import i18n.translations.frenchStrings

/**
 * Central string accessor for internationalization.
 * Use Strings.get(key) or Strings[key] to retrieve translated strings.
 */
object Strings {
    private var currentLocale: AppLocale = AppLocale.ENGLISH
    private var localeChangeListeners = mutableListOf<(AppLocale) -> Unit>()

    private val translations: Map<AppLocale, Map<StringKey, String>> = mapOf(
        AppLocale.ENGLISH to englishStrings,
        AppLocale.FRENCH to frenchStrings
    )

    /**
     * Get the current locale.
     */
    fun getLocale(): AppLocale = currentLocale

    /**
     * Set the current locale.
     * This will notify all registered listeners.
     */
    fun setLocale(locale: AppLocale) {
        if (currentLocale != locale) {
            currentLocale = locale
            localeChangeListeners.forEach { it(locale) }
        }
    }

    /**
     * Register a listener for locale changes.
     * Returns a function to unregister the listener.
     */
    fun addLocaleChangeListener(listener: (AppLocale) -> Unit): () -> Unit {
        localeChangeListeners.add(listener)
        return { localeChangeListeners.remove(listener) }
    }

    /**
     * Get a translated string for the given key.
     * Falls back to English if translation not found.
     */
    operator fun get(key: StringKey): String {
        return translations[currentLocale]?.get(key)
            ?: translations[AppLocale.ENGLISH]?.get(key)
            ?: key.name
    }

    /**
     * Get a translated string with format arguments.
     * Use %s for string placeholders, %d for integers, etc.
     */
    fun format(key: StringKey, vararg args: Any): String {
        val template = get(key)
        return if (args.isEmpty()) {
            template
        } else {
            var result = template
            args.forEachIndexed { index, arg ->
                result = result.replaceFirst("%s", arg.toString())
                result = result.replaceFirst("%d", arg.toString())
                result = result.replaceFirst("%${index + 1}\$s", arg.toString())
            }
            result
        }
    }

    /**
     * Check if a translation exists for the given key in the current locale.
     */
    fun hasTranslation(key: StringKey): Boolean {
        return translations[currentLocale]?.containsKey(key) == true
    }

    /**
     * Get all available locales.
     */
    fun getAvailableLocales(): List<AppLocale> = AppLocale.entries
}

/**
 * Extension function for convenient string access.
 * Usage: StringKey.AUTH_LOGIN.tr()
 */
fun StringKey.tr(): String = Strings[this]

/**
 * Extension function for formatted strings.
 * Usage: StringKey.SOME_KEY.tr("arg1", 42)
 */
fun StringKey.tr(vararg args: Any): String = Strings.format(this, *args)
